import { profileAuth } from '@nestle-npus-nbm/pup-script';
import { PROFILE_CONFIG_LEGACY } from 'src/common/constants';

type TokenData = { token: string };

export const ServicesAuthWrapper = <
  T extends { new (...args: any[]): { setSecurityData: (data: TokenData) => void } },
>(
  BaseClass: T,
) => {
  return class extends BaseClass {
    private token: string | null = null;

    private refreshTokenInterval: NodeJS.Timeout | null = null;

    /**
     * Initializes or refreshes the token and schedules the next refresh.
     */
    async setToken() {
      await this.handleTokenUpdate();
    }

    /**
     * Refreshes the token and schedules the next refresh.
     */
    async refreshToken() {
      await this.handleTokenUpdate();
    }

    /**
     * Handles fetching the session, updating the token, and scheduling refresh.
     */
    private async handleTokenUpdate() {
      try {
        const session = (await profileAuth(PROFILE_CONFIG_LEGACY).getSession()).rawSession;
        const accessToken = session.tokens?.accessToken?.toString() ?? null;
        this.token = accessToken;
        const expiration = session.tokens?.accessToken?.payload?.exp ?? 0;

        this.setSecurityData({ token: this.token ?? '' });
        this.stopTokenRefresh();
        this.startTokenRefresh(expiration);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating token:', error);
        throw error;
      }
    }

    /**
     * Schedules the next token refresh before expiration.
     */
    private startTokenRefresh(expiration: number) {
      this.stopTokenRefresh();
      const expiryTime = expiration - Date.now() / 1000;
      if (expiryTime > 60) {
        this.refreshTokenInterval = setTimeout(
          async () => {
            await this.refreshToken();
          },
          (expiryTime - 60) * 1000,
        );
      }
    }

    /**
     * Clears the scheduled token refresh.
     */
    private stopTokenRefresh() {
      if (this.refreshTokenInterval) {
        clearTimeout(this.refreshTokenInterval);
        this.refreshTokenInterval = null;
      }
    }
  };
};
